/**
 * Pure logic for the Catalog review screens (KG Phase 1b).
 *
 * Screens stay thin; anything worth a unit test lives here.
 */

/** Build the query string for GET /api/graph/review/products. */
export function buildQueueParams({ reviewState, page, pageSize, q, origin, audit, category, source, sort }) {
  const params = new URLSearchParams()
  params.set('review_state', reviewState)
  params.set('page', String(page))
  params.set('page_size', String(pageSize))
  const query = (q || '').trim()
  if (query) params.set('q', query)
  // 'all'/'created' are the backend defaults — omit them so review URLs stay stable.
  if (origin && origin !== 'all') params.set('origin', origin)
  if (audit && audit !== 'all') params.set('audit', audit)
  if (category) params.set('category', category)
  if (source) params.set('source', source)
  if (sort && sort !== 'created') params.set('sort', sort)
  return params.toString()
}

/**
 * Mint-audit rules, in the words an operator reads them (Prism
 * specs_20260910_kg_mint_audit). Anything unknown shows its raw key so a new
 * rule is never hidden.
 */
export const AUDIT_RULES = {
  bundle_word: 'Bundle in name',
  descriptor_tail: 'Category word in name',
  series_name: 'Series, not a model',
  near_duplicate: 'Near duplicate',
  no_image: 'No photo',
  not_standalone: 'Not a standalone product',
  name_unclean: 'Name not clean',
  brand_wrong: 'Wrong brand',
  unsure: 'Judge unsure',
  // Not the audit's: the recategorise step could not place the product
  // (Prism specs_20260928_recategorise_unsure_review). Fix via Correct.
  category_unsure: 'Category unsure'
}

export function ruleLabel(rule) {
  return AUDIT_RULES[rule] || rule
}

/**
 * POST .../correct body. The category is a KG tree sub and goes only when one
 * was picked: without it Prism leaves the product's category as it is, and
 * with it Prism also closes the product's "Category unsure" flag.
 */
export function correctBody({ brandName, model, category }) {
  const body = { brand_name: brandName.trim(), model: model.trim() }
  if (category) body.category = category
  return body
}

/**
 * Human label for a reference-image or observation source key (ADR-0033 A6
 * registry keys plus the retailer walks). Unknown keys pass through.
 */
const SOURCE_LABELS = {
  own_listing: 'Our photo',
  wikimedia_commons: 'Wikimedia Commons',
  openverse: 'Openverse',
  gear4music: 'Gear4music',
  ibanez_wiki: 'Ibanez wiki',
  customer_submission: 'Customer submission'
}

export function sourceLabel(source) {
  return SOURCE_LABELS[source] || source
}

/** "£649" / "£649.99" from an observation's minor units + ISO currency; '' without a price. */
export function formatObservedPrice(priceCents, currency) {
  if (priceCents === null || priceCents === undefined) return ''
  return formatPrice({ currency: currency || 'GBP', amount_minor: priceCents })
}

/**
 * The delete endpoint refuses a human's judgement without force: a confirmed
 * product or a pinned attachment. Those refusals say so; the others (merge
 * target, tombstone, parent) cannot be forced.
 */
export function deleteNeedsForce(message) {
  return /pass force/i.test(message || '')
}

/**
 * Engine-config PATCH body: only the fields that actually changed.
 * Numeric fields arrive from form inputs as strings — coerce before
 * comparing so an untouched "200" doesn't read as a change.
 */
const CONFIG_FIELDS = {
  adjudication_enabled: (v) => Boolean(v),
  adjudication_daily_budget: (v) => parseInt(v, 10),
  shortlist_limit: (v) => parseInt(v, 10),
  shortlist_min_similarity: (v) => parseFloat(v),
  // Retailer pipeline (Prism specs_20260910_kg_retailer_pipeline). A cap or
  // slice of 0 turns that step off; the flag ships off and is flipped here.
  retailer_pipeline_enabled: (v) => Boolean(v),
  recategorise_daily_cap: (v) => parseInt(v, 10),
  recategorise_run_slice: (v) => parseInt(v, 10),
  mint_daily_cap: (v) => parseInt(v, 10),
  mint_run_slice: (v) => parseInt(v, 10)
}

export function configDiff(saved, edited) {
  const diff = {}
  for (const [field, coerce] of Object.entries(CONFIG_FIELDS)) {
    if (!(field in edited)) continue
    const value = coerce(edited[field])
    if (typeof value === 'number' && Number.isNaN(value)) continue
    if (value !== saved[field]) diff[field] = value
  }
  return diff
}

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'CA$', AUD: 'AU$', CHF: 'CHF ', DEM: 'DM '
}
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY'])

/**
 * Render a golden-record value envelope {"value": ...} for display.
 * Strings pass through; the made-family zip becomes "Japan (1995-1996) · Korea";
 * structured money becomes "€6,029 (2022) / $299.99 (2022–2025)".
 * Unknown shapes fall back to JSON so nothing is ever hidden.
 */
export function formatGoldenValue(envelope) {
  const value = envelope?.value
  if (value === null || value === undefined) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value) && value.every(v => v && typeof v === 'object' && 'location' in v)) {
    return value
      .map(v => (v.years ? `${v.location} (${v.years})` : v.location))
      .join(' · ')
  }
  if (value && typeof value === 'object' && Array.isArray(value.prices)) {
    return value.prices.map(formatPrice).join(' / ')
  }
  return JSON.stringify(value)
}

function formatPrice(price) {
  const symbol = CURRENCY_SYMBOLS[price.currency] || `${price.currency} `
  const amount = ZERO_DECIMAL_CURRENCIES.has(price.currency)
    ? price.amount_minor
    : price.amount_minor / 100
  const text = amount.toLocaleString('en-GB', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2
  })
  const years = price.year_from
    ? price.year_from === price.year_to || !price.year_to
      ? ` (${price.year_from})`
      : ` (${price.year_from}–${price.year_to})`
    : ''
  return `${symbol}${text}${years}`
}

/** Compact evidence cell: first names, then "+n more". */
export function evidencePreview(declaredNames, max = 3) {
  const names = declaredNames || []
  if (names.length === 0) return ''
  const shown = names.slice(0, max).join(', ')
  const rest = names.length - max
  return rest > 0 ? `${shown} +${rest} more` : shown
}

/** Badge class for a product's review state (shared by the review page, detail modal and merge picker). */
export function stateBadge(reviewState) {
  if (reviewState === 'confirmed') return 'badge badge-success'
  if (reviewState === 'merged') return 'badge badge-neutral'
  return 'badge badge-warning'
}
